import React, { useEffect, useState, Fragment } from "react";
import { connect } from "react-redux";
import { Row, Col, Button, Card, CardHeader, CardBody, Progress, Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { Link } from "react-router-dom";
import _ from "lodash";

// Helpers
import NotAuthorized from "modules/5_view_helpers/web/NotAuthorized";
import HasRole from "modules/5_view_helpers/HasRole";

// Actions
import { getProjectQaPaintInspections } from "../actions";
import { toggleRightSidebarOpen } from "modules/3_reducers/web/ThemeOptions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTable } from "@fortawesome/free-solid-svg-icons";

import "./index.css";
import AttachmentViewer from "modules/5_view_helpers/web/AttachmentViewer";
import classnames from "classnames";
import { getPaintLocations } from "modules/paint/locations/actions";
import TableTorch from "modules/5_view_helpers/web/TableTorch";

function mapStateToProps(state, props) {
  const project_id = props.match?.params?.project_id || props.project_id;

  return {
    project: state.projects[project_id],
    this_project_qa_paint_inspections: _.filter(state.project_qa_paint_inspections, (pi) => pi.project_id == project_id),
    rightSidebarOpen: state.ThemeOptions.rightSidebarOpen,
    paint_locations: state.paint_locations,
  };
}

const ProjectQaPaintInspectionsIndex = (props) => {
  const [selected, setSelected] = useState([]);
  const [activeTab, setActiveTab] = useState("");
  const { paint_locations, showNewButton, showSelection, onSelected } = props;

  const project_id = props.match?.params?.project_id || props.project_id;

  useEffect(() => {
    if (showSelection && onSelected) {
      onSelected(selected);
    }
  }, [selected]);

  useEffect(() => {
    props.getPaintLocations();
  }, []);

  useEffect(() => {
    if (_.isEmpty(activeTab) && _.isNotEmpty(paint_locations)) {
      const findDefault = _.find(paint_locations, (pl) => pl.default);
      setActiveTab(findDefault?.id);
    }
  }, [paint_locations]);

  const { this_project_qa_paint_inspections } = props;
  useEffect(() => {
    if (project_id && _.isEmpty(this_project_qa_paint_inspections)) {
      props.getProjectQaPaintInspections(project_id);
    }
  }, [project_id]);

  const toggle = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  return (
    <div>
      <Nav tabs>
        {_.map(paint_locations, (paint_location, i) => (
          <NavItem key={i}>
            <NavLink
              className={classnames({ active: activeTab === paint_location.id })}
              onClick={() => {
                toggle(paint_location.id);
              }}
            >
              {paint_location.name}
            </NavLink>
          </NavItem>
        ))}
      </Nav>
      <TabContent activeTab={activeTab}>
        {_.map(paint_locations, (paint_location, i) => {
          const this_location_paint_inspections = _.filter(this_project_qa_paint_inspections, (pi) => pi.location_id == paint_location.id);

          return (
            <TabPane tabId={paint_location?.id} key={i}>
              {activeTab == paint_location.id && (
                <ProjectQaPaintInspectionsByLocation
                  showNewButton={showNewButton}
                  paint_inspections={this_location_paint_inspections}
                  project_id={project_id}
                  showSelection={showSelection}
                  setSelected={setSelected}
                  selected={selected}
                  {...props}
                />
              )}
            </TabPane>
          );
        })}
      </TabContent>
    </div>
  );
};

ProjectQaPaintInspectionsIndex.defaultProps = {
  showNewButton: true,
};

export default connect(mapStateToProps, { getProjectQaPaintInspections, toggleRightSidebarOpen, getPaintLocations })(ProjectQaPaintInspectionsIndex);

const ProjectQaPaintInspectionsByLocation = (props) => {
  const { project_id, project, paint_inspections, showNewButton } = props;

  const readRole = HasRole({ resource_type: "Project::Qa::PaintInspection", name: ["read", "moderator"] });
  const createRole = HasRole({ resource_type: "Project::Qa::PaintInspection", name: ["create", "moderator"] });
  const updateRole = HasRole({ resource_type: "Project::Qa::PaintInspection", name: ["update", "moderator"] });

  const [reportModalOpen, setReportModalOpen] = useState(false); // Report Modal
  const toggleReportModal = () => setReportModalOpen(!reportModalOpen);

  const ordered_paint_inspections = React.useMemo(
    () => _.orderBy(paint_inspections, ["assembly.mark", "phase", "lot", "number"], ["asc", "asc", "asc", "asc"]),
    [paint_inspections],
  );

  let columns = [
    {
      header: "Assembly",
      dataKey: "assembly.display_name_with_guid",
      sort: true,
      disableReportOption: true,
    },
    {
      header: "Phase",
      dataKey: "assembly.phase",
      sort: true,
      className: "w-50px",
      disableReportOption: true,
    },
    {
      header: "Lot",
      dataKey: "assembly.lot",
      sort: true,
      className: "w-50px",
      disableReportOption: true,
    },
    {
      header: "Paint System",
      dataKey: "paint_system_display_code",
      sort: true,
      disableReportOption: true,
    },
    {
      header: "Inspection #",
      dataKey: "number",
      sort: true,
      disableReportOption: true,
    },
    {
      header: "Coat",
      dataKey: "coat_display_name",
      sort: true,
      disableReportOption: true,
    },
    {
      header: "Inspection Location",
      dataKey: "location_name",
      sort: true,
      disableReportOption: true,
    },
    {
      header: "Date",
      dataKey: "created_at",
      sort: true,
      cellRenderer: (cellData) => {
        return <>{fd(cellData)}</>;
      },
      disableReportOption: true,
    },
    {
      header: "Average DFT",
      dataKey: "average_dft",
      sort: true,
      className: "w-75px",
    },
    {
      header: "Low DFT",
      dataKey: "low_dft",
      sort: true,
      className: "w-75px",
    },
    {
      header: "High DFT",
      dataKey: "high_dft",
      sort: true,
      className: "w-75px",
    },
    {
      header: "# Readings",
      dataKey: "readings",
      sort: true,
    },
    {
      header: "Complies",
      dataKey: "complies",
      cellRenderer: (cellData, row) => {
        if (row.inspect_by_sections) {
          return row.section_readings_attributes.every((sr) => sr.complies) ? "Yes" : "No";
        } else {
          return cellData ? "Yes" : "No";
        }
      },
    },
  ];

  if (updateRole) {
    columns.push({
      header: "Edit",
      dataKey: "edit",
      cellRenderer: (cell, row) => {
        return <Link to={`/project/${row?.assembly?.project_id}/qa/paint_inspections/${row.id}/edit`}>Edit</Link>;
      },
      print: false,
    });
  }

  if (!readRole) {
    return <NotAuthorized />;
  } else {
    return (
      <Fragment>
        <Card>
          <CardHeader>
            <Row>
              <Col>
                <h3>{project?.display_name} Paint inspections</h3>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <Row>
              {createRole && showNewButton && (
                <Col>
                  <Link to={`/project/${project_id}/qa/paint_inspections/new`}>
                    <Button color="primary" size="md">
                      <i className="fa fa-plus"> Create New Paint inspection</i>
                    </Button>
                  </Link>
                </Col>
              )}
            </Row>

            {_.isNotEmpty(ordered_paint_inspections) && (
              <TableTorch
                columns={columns}
                data={ordered_paint_inspections}
                selectable
                showCreateReport
                reportURL="paint_inspections.pdf"
                reportModal={true}
                reportModalButtonLabel="Get Overall Report"
                initialGroupBy={[{ label: "Assembly", value: "assembly.display_name_with_guid" }]}
                childComponent={(row) => {
                  if (row?.inspect_by_sections) {
                    return (
                      <table style={{ tableLayout: "fixed", width: "100%", border: "2px solid" }}>
                        <tr>
                          <th colSpan="5" className="text-center">
                            Section Readings
                          </th>
                        </tr>
                        <tr>
                          <th>Section</th>
                          <th>Average</th>
                          <th>Low</th>
                          <th>High</th>
                          <th># Readings</th>
                          <th>Complies</th>
                        </tr>
                        {_.map(row?.section_readings_attributes, (section) => {
                          return (
                            <tr>
                              <td>{section?.section_size}</td>
                              <td>{section?.average_dft}</td>
                              <td>{section?.low_dft}</td>
                              <td>{section?.high_dft}</td>
                              <td>{section?.readings}</td>
                              <td>{section?.complies ? "Yes" : "No"}</td>
                            </tr>
                          );
                        })}
                      </table>
                    );
                  } else {
                    return null;
                  }
                }}
              />
            )}
          </CardBody>
        </Card>
      </Fragment>
    );
  }
};
